/**
 * PacingBar — at-a-glance delivery pacing (Design System §5).
 *
 * A horizontal track with a fill (% delivered vs contracted) and a target
 * marker at the expected % (time elapsed). Color reflects pacing health per
 * the §4.3 status matrix; an over-delivery (delivered ≥ contracted) is
 * flagged prominently because over-delivering is the thing the team most
 * needs to avoid.
 *
 * No chart library — matches the house style (hand-rolled div/SVG widgets).
 */

import React from 'react';

/**
 * Structural subset of Invoicing's DeliverySnapshot — anything with these
 * fields (DeliverySnapshot included) is assignable.
 */
export interface PacingSnapshot {
  percentDelivered?: number;
  progressPercent?: number;
  expectedPercent?: number;
  isPacingAhead?: boolean;
  isPacingBehind?: boolean;
  daysRemaining?: number;
  overDeliveryRatio?: number;
  overDeliveryFlag?: string;
  impressionsDelivered1P?: number;
  impressionsDelivered3P?: number;
  lastPacingSyncAt?: string | null;
}

interface Props {
  delivery: PacingSnapshot;
  /** 'compact' for table cells, 'panel' for the expanded detail. */
  variant?: 'compact' | 'panel';
  /** Contracted impressions, for the panel readout. */
  contracted?: number;
  /** Shown when there's no pacing data yet (default "Not synced"). */
  noDataLabel?: string;
}

export const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));

export function fillColor(d: PacingSnapshot): string {
  if (d.overDeliveryFlag === 'exceeded') return 'var(--danger)';
  if (d.isPacingBehind) return 'var(--danger)';
  if (d.isPacingAhead || d.overDeliveryFlag === 'approaching') return 'var(--warning)';
  return 'var(--success)';
}

export const fmtInt = (n: number | undefined) => (n === undefined ? '—' : Math.round(n).toLocaleString());
export const fmtPct = (n: number | undefined) => (n === undefined ? '—' : `${n.toFixed(0)}%`);

/** The bare track + fill + expected-time marker, shared by every pacing visual. */
export const PacingTrack: React.FC<{
  /** Fill %, clamped to [0, 100] here. */
  percent: number;
  /** Expected-by-now marker position (omitted = no marker). */
  expected?: number;
  /** Fill color (a CSS color, e.g. 'var(--success)'). */
  color: string;
  height?: number;
}> = ({ percent, expected, color, height = 8 }) => (
  <div style={{ ...styles.track, height }}>
    <div style={{ ...styles.fill, width: `${clamp(percent, 0, 100)}%`, background: color }} />
    {expected !== undefined && (
      <div
        style={{ ...styles.marker, left: `${clamp(expected, 0, 100)}%` }}
        title={`Expected ${fmtPct(expected)} by now`}
      />
    )}
  </div>
);

const PacingBar: React.FC<Props> = ({ delivery, variant = 'compact', contracted, noDataLabel }) => {
  const pct = delivery.percentDelivered;
  const expected = delivery.expectedPercent;
  const hasData = pct !== undefined;
  const color = fillColor(delivery);
  const exceeded = delivery.overDeliveryFlag === 'exceeded';

  if (!hasData) {
    return <span style={styles.noData}>{noDataLabel ?? 'Not synced'}</span>;
  }

  const track = <PacingTrack percent={pct ?? 0} expected={expected} color={color} />;

  if (variant === 'compact') {
    return (
      <div style={styles.compactWrap} title={`${fmtPct(pct)} delivered · expected ${fmtPct(expected)}`}>
        {track}
        <div style={styles.compactLabels}>
          <span style={{ color, fontWeight: 600 }}>{fmtPct(pct)}</span>
          {exceeded && <span style={styles.overFlag}>OVER</span>}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.panelWrap}>
      {exceeded && (
        <div style={styles.overBanner}>
          ⚠ OVER-DELIVERED — delivered impressions have reached or exceeded the contracted goal.
        </div>
      )}
      {track}
      <div style={styles.panelGrid}>
        <Readout label="Delivered" value={fmtPct(pct)} color={color} />
        <Readout label="Time elapsed" value={fmtPct(expected)} />
        <Readout label="On-target" value={fmtPct(delivery.progressPercent)} />
        <Readout label="Days left" value={delivery.daysRemaining?.toString() ?? '—'} />
        <Readout label="1P delivered" value={fmtInt(delivery.impressionsDelivered1P)} />
        <Readout label="3P delivered" value={fmtInt(delivery.impressionsDelivered3P)} />
        <Readout label="Contracted" value={fmtInt(contracted)} />
        <Readout
          label="Over-delivery"
          value={
            delivery.overDeliveryRatio !== undefined
              ? `${(delivery.overDeliveryRatio * 100).toFixed(0)}%`
              : '—'
          }
          color={exceeded ? 'var(--danger)' : delivery.overDeliveryFlag === 'approaching' ? 'var(--warning)' : undefined}
        />
      </div>
      {delivery.lastPacingSyncAt && (
        <div style={styles.synced}>
          Pacing synced {new Date(delivery.lastPacingSyncAt).toLocaleString()}
        </div>
      )}
    </div>
  );
};

const Readout: React.FC<{ label: string; value: string; color?: string }> = ({ label, value, color }) => (
  <div style={styles.readout}>
    <div style={styles.readoutLabel}>{label}</div>
    <div style={{ ...styles.readoutValue, ...(color ? { color } : {}) }}>{value}</div>
  </div>
);

const styles: Record<string, React.CSSProperties> = {
  noData: { fontSize: 11.5, color: 'var(--fg-4)', fontStyle: 'italic' },
  compactWrap: { display: 'flex', flexDirection: 'column', gap: 3, minWidth: 96 },
  compactLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 11,
    fontVariantNumeric: 'tabular-nums',
  },
  track: {
    position: 'relative',
    height: 8,
    width: '100%',
    background: 'var(--bg-surface-3)',
    borderRadius: 'var(--radius-pill)',
    overflow: 'hidden',
  },
  fill: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    borderRadius: 'var(--radius-pill)',
    transition: 'width 200ms var(--ease-standard)',
  },
  marker: {
    position: 'absolute',
    top: -2,
    bottom: -2,
    width: 2,
    background: 'var(--fg-2)',
    transform: 'translateX(-1px)',
  },
  overFlag: {
    fontSize: 9.5,
    fontWeight: 800,
    letterSpacing: '0.06em',
    color: 'var(--danger)',
  },
  panelWrap: { display: 'flex', flexDirection: 'column', gap: 12 },
  overBanner: {
    padding: '8px 12px',
    background: 'var(--danger-soft)',
    color: 'var(--danger)',
    borderRadius: 'var(--radius-md)',
    fontSize: 12.5,
    fontWeight: 600,
    border: '1px solid var(--cg-terra-200)',
  },
  panelGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
    gap: 12,
  },
  readout: { display: 'flex', flexDirection: 'column', gap: 2 },
  readoutLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: 'var(--fg-4)',
  },
  readoutValue: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--fg-1)',
    fontVariantNumeric: 'tabular-nums',
  },
  synced: { fontSize: 11, color: 'var(--fg-4)' },
};

export default PacingBar;
